import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NodeShapeDTO } from '../../models/ShaclShapeSummary';
import { ActivatedRoute } from '@angular/router';
import { ShapeDataService } from '../../services/shape-data.service';
import { ShaclApiService } from '../../services/shacl-api.service';
import { UserService } from 'src/app/services/user-service/userservice';
import { sanitizeFilename, generateTimestampedFilename, downloadJsonFile } from '../../utils/file-download.util';
import { finalize } from 'rxjs/operators';
import { FormGroup } from '@angular/forms';
import {
  TableColumn,
  TableEmptyState,
  TableInspector,
  TablePageAction,
  TablePageActionEvent,
  TableRowAction,
  TableRowActionEvent,
  TableSummaryItem
} from 'src/app/con-x/table/table-page.component';

interface SavedSdRow {
  id: string | number;
  name: string;
  description?: string | null;
  createdAt?: string | null;
  content?: string | Record<string, unknown> | null;
}

interface SdShapeOption {
  id: string;
  targetClass: string;
  name: string;
  description?: string | null;
}


@Component({
  selector: 'app-sdshapes-list',
  standalone: false,
  templateUrl: './sdshapes-list.component.html',
  styleUrls: ['./sdshapes-list.component.css'],
})
export class SDShapesListComponent implements OnInit {

  @Output() formValue = new EventEmitter<FormGroup>();
  @ViewChild('sdDocumentDialog', { static: true }) private sdDocumentDialog!: TemplateRef<unknown>;

  shapes: SdShapeOption[] = [];
  shapesLoading = false;
  shapesError = '';
  savedSds: SavedSdRow[] = [];
  savedSdsLoading = false;
  savedSdsError = '';
  error?: string;
  selectedIri?: string;
  detailJson?: unknown;
  detailLoading = false;
  detailError?: string;
  selectedDescription?: string;
  selectedName?: string;
  selectedShape?: NodeShapeDTO;
  isCreateMode = false;
  createShapeForm: boolean;
  dialogSd: SavedSdRow | null = null;
  dialogJson = '';
  sdCopied = false;
  private sdDialogRef: MatDialogRef<unknown> | null = null;

  readonly savedSdColumns: readonly TableColumn<SavedSdRow>[] = [
    { key: 'name', label: 'Name' },
    { key: 'description', label: 'Beschreibung', emptyText: '—' },
    { key: 'createdAt', label: 'Erstellt', type: 'datetime', emptyText: 'Nicht geliefert' }
  ];

  readonly pageActions: readonly TablePageAction[] = [
    { id: 'create', label: 'Neue SD erstellen', icon: 'add', appearance: 'flat', tone: 'primary' }
  ];

  readonly savedSdActions: readonly TableRowAction<SavedSdRow>[] = [
    { id: 'view', label: 'SD anzeigen', icon: 'visibility' },
    { id: 'download', label: 'JSON herunterladen', icon: 'download' }
  ];

  readonly savedSdInspector: TableInspector<SavedSdRow> = {
    ariaLabel: 'Details der ausgewählten Self-Description',
    title: 'name',
    subtitle: row => `Self-Description ${row.id}`,
    icon: 'description',
    tone: 'primary',
    fields: [
      { label: 'Beschreibung', value: 'description', emptyText: '—' },
      { label: 'Erstellt', value: 'createdAt', type: 'datetime', emptyText: 'Nicht geliefert' }
    ],
    actions: this.savedSdActions
  };

  readonly savedSdEmptyState: TableEmptyState = {
    icon: 'description',
    title: 'Keine Self-Descriptions gespeichert',
    description: 'Erstellen Sie eine neue Self-Description, um sie hier zu verwalten.',
  };

  get summaryItems(): readonly TableSummaryItem<SavedSdRow>[] {
    return [{ label: 'Self-Descriptions', value: this.savedSds.length, icon: 'description' }];
  }

  constructor(
    private route: ActivatedRoute,
    private api: ShaclApiService,
    private shapeDataService: ShapeDataService,
    private userService: UserService,
    private cd: ChangeDetectorRef,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    const data = this.route.snapshot.data['shapes'];
    if (Array.isArray(data)) {
      this.setShapes(data);
    } else if (data?.error) {
      this.shapesError = 'Die verfügbaren Shapes konnten nicht geladen werden.';
    }

    // Check for organisationId in URL params
    const orgId = this.route.snapshot.params['organisationId'] || this.route.snapshot.parent?.params['organisationId'];

    if (orgId) {
      console.log('Organisation context detected:', orgId);
      this.fetchSavedSds(orgId, true);
    } else {
      const userId = this.userService.currentUser?.userId;
      if (userId) {
        console.log('User context detected:', userId);
        this.fetchSavedSds(userId.toString(), false);
      } else {
        this.savedSds = [];
      }
    }
  }

  fetchSavedSds(id: string | undefined, isOrg: boolean) {
    if (!id || id === 'undefined') {
      this.savedSds = [];
      return;
    }

    const request = isOrg
      ? this.api.getSavedSdsByOrgId(id)
      : this.api.getSavedSdsByUserId(id);

    this.savedSdsLoading = true;
    this.savedSdsError = '';
    request.pipe(finalize(() => {
      this.savedSdsLoading = false;
      this.cd.detectChanges();
    })).subscribe({
      next: (sds) => {
        this.savedSds = sds;
      },
      error: (err) => {
        console.error(`Failed to fetch saved SDs for ${isOrg ? 'org' : 'user'}`, err);
        this.savedSdsError = 'Die gespeicherten Self-Descriptions konnten nicht geladen werden.';
      }
    });
  }

  handlePageAction(event: TablePageActionEvent): void {
    if (event.actionId === 'create') this.startCreateMode();
  }

  handleSavedSdAction(event: TableRowActionEvent<SavedSdRow>): void {
    if (event.actionId === 'view') this.onViewSd(event.row);
    if (event.actionId === 'download') this.onDownloadSd(event.row);
  }

  startCreateMode(): void {
    this.isCreateMode = true;
    this.detailJson = undefined;
    this.selectedName = undefined;
    this.selectedDescription = undefined;
    this.selectedIri = undefined;
    this.selectedShape = undefined;
    if (!this.shapes.length) this.loadShapes();
  }

  onShapeDropdownOpened(opened: boolean): void {
    if (opened && !this.shapes.length && !this.shapesLoading) this.loadShapes();
  }

  loadShapes(): void {
    if (this.shapesLoading) return;

    this.shapesLoading = true;
    this.shapesError = '';
    this.api.listShapes().pipe(finalize(() => {
      this.shapesLoading = false;
      this.cd.detectChanges();
    })).subscribe({
      next: shapes => this.setShapes(shapes),
      error: error => {
        console.error('Failed to load SD shapes:', error);
        this.shapesError = 'Die verfügbaren Shapes konnten nicht geladen werden.';
      }
    });
  }

  onShapeSelected(selectedIri: string): void {
    this.createShapeForm = false;
    const shape = this.shapes.find(s => s.targetClass === selectedIri);
    if (shape) {
      this.selectShape(shape.targetClass, shape.name, shape.description);
    }
  }

  selectShape(iri: string, name: string, description?: string | null): void {
    this.selectedName = name;
    this.selectedDescription = description ?? undefined;
    this.selectedIri = iri;
    this.detailJson = undefined;
    this.detailError = undefined;
    this.detailLoading = true;
    this.api.getShape(iri).subscribe({
      next: (shape) => {
        this.selectedShape = shape;
        this.detailJson = shape;
        this.detailLoading = false;
      },
      error: (err) => {
        this.detailError = 'Failed to load shape details';
        this.detailLoading = false;
      }
    });
  }

  onViewSd(sd: SavedSdRow): void {
    const content = this.parseSavedSdContent(sd);
    if (!content) return;

    this.sdDialogRef?.close();
    this.dialogSd = sd;
    this.dialogJson = JSON.stringify(content, null, 2);
    this.sdCopied = false;
    this.sdDialogRef = this.dialog.open(this.sdDocumentDialog, {
      width: '56rem',
      maxWidth: 'calc(100vw - 2rem)',
      maxHeight: 'calc(100dvh - 2rem)',
      panelClass: 'sd-document-dialog-panel',
      autoFocus: false,
      restoreFocus: true
    });
    this.sdDialogRef.afterClosed().subscribe(() => {
      this.dialogSd = null;
      this.dialogJson = '';
      this.sdCopied = false;
      this.sdDialogRef = null;
    });
  }

  onDownloadSd(sd: SavedSdRow): void {
    const content = this.parseSavedSdContent(sd);
    if (!content) return;

    const baseFilename = sanitizeFilename(sd.name || 'credential');
    const filename = generateTimestampedFilename(baseFilename, 'json');
    downloadJsonFile(content, filename, true);
  }

  copyOpenSd(): void {
    if (!this.dialogJson) return;

    navigator.clipboard.writeText(this.dialogJson).then(() => {
      this.sdCopied = true;
      this.cd.detectChanges();
    }).catch(error => console.error('Failed to copy saved SD content:', error));
  }

  downloadOpenSd(): void {
    if (this.dialogSd) this.onDownloadSd(this.dialogSd);
  }

  trackByIri(index: number, shape: SdShapeOption): string {
    return shape.targetClass;
  }

  private setShapes(shapes: readonly NodeShapeDTO[]): void {
    this.shapes = shapes
      .map(shape => this.toShapeOption(shape))
      .filter((shape): shape is SdShapeOption => shape !== null)
      .sort((left, right) => left.name.localeCompare(right.name, 'de'));
    this.shapesError = this.shapes.length ? '' : 'Es wurden keine verfügbaren Shapes gefunden.';
  }

  private toShapeOption(shape: NodeShapeDTO & { description?: string | null }): SdShapeOption | null {
    const targetClass = (shape.targetClass || shape.id || '').trim();
    if (!targetClass) return null;

    return {
      id: shape.id || targetClass,
      targetClass,
      name: shape.name?.trim() || this.readableShapeName(targetClass),
      description: shape.description
    };
  }

  private readableShapeName(iri: string): string {
    const fragment = iri.split(/[/#]/).filter(Boolean).pop() || iri;
    return fragment
      .replace(/Shape$/, '')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .trim() || iri;
  }

  private parseSavedSdContent(sd: SavedSdRow): Record<string, unknown> | null {
    if (sd.content && typeof sd.content === 'object') return sd.content;
    if (typeof sd.content !== 'string' || !sd.content.trim()) {
      this.savedSdsError = `Für „${sd.name}“ ist kein SD-Inhalt verfügbar.`;
      return null;
    }

    try {
      const parsed = JSON.parse(sd.content);
      if (parsed && typeof parsed === 'object') return parsed as Record<string, unknown>;
    } catch (error) {
      console.error('Failed to parse saved SD content:', error);
    }

    this.savedSdsError = `Der Inhalt von „${sd.name}“ ist kein gültiges JSON-Dokument.`;
    return null;
  }

  createForm(): void {
    if (!this.selectedIri || !this.selectedShape) return;

    // Store the shape data in the service
    this.shapeDataService.setSelectedShape(
      this.selectedShape,
      this.selectedName,
      this.selectedDescription
    );

    this.createShapeForm = true
  }

  emitFormValue(formValue: FormGroup): void {
    this.formValue.emit(formValue);
  }
}
